import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createApiClient } from "../lib/apiClient";
import { useUserId } from "../UserIdContext";
import { mergeDistinctStrings, normalizeStringList } from "./unidadesNegocioApi";
import "./ingreso.css";

const api = createApiClient({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:3220",
  headers: { "Content-Type": "application/json" },
});

const API = {
  CLIENTES_BUSCAR: "/api/clientes/buscar",
  PALLETS: "/api/pallets",
  RESERVAR: "/api/egresos/carrito/reservar",
};

const EGRESS_COLUMN_WIDTHS = {
  nroRemito: 120,
  ubicacion: 140,
  cliente: 160,
  fechaIngreso: 120,
  procedencia: 180,
  unidadNegocio: 170,
  compIng: 110,
  fcGrupoProveedor: 190,
  fechaElab: 110,
  fechaVto: 110,
  codigo: 120,
  detalleFc: 220,
  nroLote: 110,
  id: 100,
  nroPallet: 120,
  tipoEmbalaje: 150,
  senasa: 120,
  unidPallet: 110,
  pesoMedidas: 150,
  cantFc: 110,
  daniosIn: 90,
  motivoDanio: 170,
  obsIn: 220,
  duplicar: 90,
  reservado: 90,
};

const EGRESS_COLUMN_WIDTHS_STORAGE_KEY = "egreso-maqueta-column-widths";
const EGRESS_COLUMN_MIN_WIDTH = 70;

const getInitialEgressColumnWidths = () => {
  if (typeof window === "undefined") {
    return { ...EGRESS_COLUMN_WIDTHS };
  }
  try {
    const raw = window.localStorage.getItem(EGRESS_COLUMN_WIDTHS_STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : null;
    if (!stored || typeof stored !== "object") {
      return { ...EGRESS_COLUMN_WIDTHS };
    }

    const next = { ...EGRESS_COLUMN_WIDTHS };
    Object.keys(EGRESS_COLUMN_WIDTHS).forEach((key) => {
      const width = Number(stored[key]);
      if (Number.isFinite(width) && width >= EGRESS_COLUMN_MIN_WIDTH) {
        next[key] = width;
      }
    });
    return next;
  } catch {
    return { ...EGRESS_COLUMN_WIDTHS };
  }
};

const buildColStyle = (width) => ({
  width: `${width}px`,
  minWidth: `${width}px`,
});

function fmtDate(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

const normalizarFiltros = (arr) => {
  if (!Array.isArray(arr)) return [];
  const set = new Set();
  arr.forEach((v) => {
    const s = String(v || "").trim();
    if (s) set.add(s);
  });
  return Array.from(set);
};

const normalizarTexto = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/n[\u00ba\u00b0]/gi, "nro")
    .replace(/[\u00ba\u00b0]/g, "")
    .replace(/\bno\b/gi, "nro")
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .toLowerCase();

const normalizeClienteCode = (value) =>
  String(value || "").trim().toLowerCase();

const toNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const INITIAL_FILTER_INPUTS = {
  nroRemito: "",
  fechaIngreso: "",
  ubicacion: "",
  planta: "",
  unidadNegocio: "",
  lote: "",
  codigo: "",
  detalleProducto: "",
  fechaElaboracion: "",
  fechaVencimiento: "",
  tipoEmbalaje: "",
  recintoSenasa: "",
  condicion: "",
  observaciones: "",
  unidadesPorPallet: "",
  pesoMedidas: "",
};

const resolveClientePlanta = (cliente) => {
  const procedencia = String(cliente?.procedencia ?? "").trim();
  if (procedencia) return procedencia;
  return String(cliente?.planta ?? "").trim();
};

const matchesTextFilter = (value, query) => {
  const normalizedQuery = normalizarTexto(query);
  if (!normalizedQuery) return true;
  return normalizarTexto(value).includes(normalizedQuery);
};

const matchesDateFilter = (value, query) => {
  if (!query) return true;
  return fmtDate(value) === query;
};

const EgresoMaqueta = () => {
  const { deposito } = useUserId();
  const depositoActual = deposito || "binder1";
  const navigate = useNavigate();
  const [pallets, setPallets] = useState([]);
  const [mostrarModal, setMostrarModal] = useState(false);

  const [clienteQuery, setClienteQuery] = useState("");
  const [clientes, setClientes] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);
  const [filtrosSel, setFiltrosSel] = useState([]);
  const [filtrosInputs, setFiltrosInputs] = useState(INITIAL_FILTER_INPUTS);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [loadingPallets, setLoadingPallets] = useState(false);
  const dragReservaRef = useRef({ active: false, value: false });
  const tableScrollRef = useRef(null);
  const tableTopScrollRef = useRef(null);
  const tableBottomScrollRef = useRef(null);
  const [columnWidths, setColumnWidths] = useState(getInitialEgressColumnWidths);
  const [tableScrollW, setTableScrollW] = useState(0);
  const [tableClientW, setTableClientW] = useState(0);
  const tieneFiltrosConfigurados = filtrosSel.length > 0;
  const clienteSeleccionadoLabel =
    clienteSel?.nombreCliente || clienteSel?.codCliente || "";
  const clienteSeleccionadoCoincide =
    Boolean(clienteSel?._id) &&
    normalizarTexto(clienteQuery) === normalizarTexto(clienteSeleccionadoLabel);

  const mostrarCampo = (nombre) =>
    filtrosSel.length === 0
      ? true
      : filtrosSel.some(
          (f) => normalizarTexto(f) === normalizarTexto(nombre)
        );

  const resetFiltrosInputs = () => {
    setFiltrosInputs({ ...INITIAL_FILTER_INPUTS });
  };

  const handleFiltroInputChange = (e) => {
    const { name, value } = e.target;
    setFiltrosInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectCliente = (cliente) => {
    setClienteSel(cliente);
    setClienteQuery(cliente?.nombreCliente || "");
    setFiltrosSel(normalizarFiltros(cliente?.Filtros));
    resetFiltrosInputs();
    setClientes([]);
  };

  useEffect(() => {
    const q = clienteQuery.trim();
    if (!q) {
      setClientes([]);
      return;
    }
    if (clienteSeleccionadoCoincide) {
      setClientes([]);
      setBuscandoCliente(false);
      return;
    }
    const t = setTimeout(async () => {
      setBuscandoCliente(true);
      try {
        const { data } = await api.get(API.CLIENTES_BUSCAR, { params: { q } });
        setClientes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("GET /api/clientes/buscar", err);
        setClientes([]);
      } finally {
        setBuscandoCliente(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [clienteQuery, clienteSeleccionadoCoincide]);

  const clientesSugeridos = useMemo(() => {
    const queryCode = normalizeClienteCode(clienteQuery);
    if (!queryCode) return clientes;

    const exactCodeMatches = clientes.filter(
      (cliente) => normalizeClienteCode(cliente?.codCliente) === queryCode
    );

    return exactCodeMatches.length > 0 ? exactCodeMatches : clientes;
  }, [clienteQuery, clientes]);

  const fetchPallets = useCallback(async (clienteId) => {
    if (!clienteId) {
      setPallets([]);
      return;
    }
    setLoadingPallets(true);
    try {
      const { data } = await api.get(API.PALLETS, {
        params: { clienteId, limit: 500, deposito: depositoActual },
      });
      const items = data?.data?.items || [];
      const palletsUI = items.map((p) => ({
        ...p,
        reserva: false,
        duplicarPallet: false,
        esDuplicado: false,
      }));
      setPallets(palletsUI);
    } catch (err) {
      console.error("GET /api/pallets", err);
      setPallets([]);
    } finally {
      setLoadingPallets(false);
    }
  }, [depositoActual]);

  useEffect(() => {
    if (clienteSel?._id) {
      fetchPallets(clienteSel._id);
    } else {
      setPallets([]);
    }
  }, [clienteSel, fetchPallets]);

  useEffect(() => {
    const stopDrag = () => {
      dragReservaRef.current.active = false;
    };
    window.addEventListener("mouseup", stopDrag);
    return () => window.removeEventListener("mouseup", stopDrag);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      EGRESS_COLUMN_WIDTHS_STORAGE_KEY,
      JSON.stringify(columnWidths)
    );
  }, [columnWidths]);

  const palletsConDatosFiltro = useMemo(
    () =>
      pallets.map((p, index) => {
        const producto = p?.producto || {};
        const lote = p?.lote || {};
        const ingreso = p?.ingreso || {};
        const danios = p?.danios || {};
        return {
          index,
          item: p,
          valores: {
            nroRemito:
              ingreso?.nroRemito || ingreso?.remito || p?.nroRemito || p?.remito || "",
            fechaIngreso: p?.fechaIngreso,
            ubicacion: p?.ubicacion || ingreso?.ubicacionTexto || "",
            planta:
              p?.procedenciaPlanta ||
              p?.plantaTexto ||
              ingreso?.planta ||
              "",
            unidadNegocio:
              p?.unidad ||
              p?.unidadNegocioTexto ||
              ingreso?.unidadNegocio ||
              "",
            lote: lote?.nroLote || p?.loteTexto || "",
            codigo: producto?.codProducto || p?.codigo || "",
            detalleProducto:
              producto?.detalleProducto ||
              producto?.nombreProducto ||
              p?.detalleProductoTexto ||
              p?.nroFc ||
              "",
            fechaElaboracion: lote?.fechaFab || p?.fechaElaboracion,
            fechaVencimiento: lote?.fechaVto || p?.fechaVencimiento,
            tipoEmbalaje: p?.tipoEmbalaje || "",
            recintoSenasa: p?.senasaCodigo || p?.recintoSenasa || "",
            condicion: p?.condicion || "",
            observaciones: [
              p?.observacionesIngreso,
              danios?.obsIn,
              producto?.obsGeneral,
            ]
              .filter(Boolean)
              .join(" | "),
            unidadesPorPallet: String(p?.cantidad ?? p?.unidadesPorPallet ?? ""),
            pesoMedidas: [producto?.peso, producto?.medidas, p?.pesoMedidas]
              .filter(Boolean)
              .join(" / "),
          },
        };
      }),
    [pallets]
  );

  const ubicacionesDisponibles = useMemo(
    () =>
      mergeDistinctStrings(
        normalizeStringList(clienteSel?.ubicacion),
        palletsConDatosFiltro.map((entry) => entry.valores.ubicacion)
      ),
    [clienteSel, palletsConDatosFiltro]
  );

  const plantasDisponibles = useMemo(
    () =>
      mergeDistinctStrings(
        resolveClientePlanta(clienteSel),
        palletsConDatosFiltro.map((entry) => entry.valores.planta)
      ),
    [clienteSel, palletsConDatosFiltro]
  );

  const unidadesNegocioDisponibles = useMemo(
    () =>
      mergeDistinctStrings(
        normalizeStringList(clienteSel?.unidadNegocio),
        palletsConDatosFiltro.map((entry) => entry.valores.unidadNegocio)
      ),
    [clienteSel, palletsConDatosFiltro]
  );

  const palletsVisibles = useMemo(
    () =>
      palletsConDatosFiltro
        .filter(({ valores }) => {
          return (
            matchesTextFilter(valores.nroRemito, filtrosInputs.nroRemito) &&
            matchesDateFilter(valores.fechaIngreso, filtrosInputs.fechaIngreso) &&
            matchesTextFilter(valores.ubicacion, filtrosInputs.ubicacion) &&
            matchesTextFilter(valores.planta, filtrosInputs.planta) &&
            matchesTextFilter(
              valores.unidadNegocio,
              filtrosInputs.unidadNegocio
            ) &&
            matchesTextFilter(valores.lote, filtrosInputs.lote) &&
            matchesTextFilter(valores.codigo, filtrosInputs.codigo) &&
            matchesTextFilter(
              valores.detalleProducto,
              filtrosInputs.detalleProducto
            ) &&
            matchesDateFilter(
              valores.fechaElaboracion,
              filtrosInputs.fechaElaboracion
            ) &&
            matchesDateFilter(
              valores.fechaVencimiento,
              filtrosInputs.fechaVencimiento
            ) &&
            matchesTextFilter(
              valores.tipoEmbalaje,
              filtrosInputs.tipoEmbalaje
            ) &&
            matchesTextFilter(
              valores.recintoSenasa,
              filtrosInputs.recintoSenasa
            ) &&
            matchesTextFilter(valores.condicion, filtrosInputs.condicion) &&
            matchesTextFilter(
              valores.observaciones,
              filtrosInputs.observaciones
            ) &&
            matchesTextFilter(
              valores.unidadesPorPallet,
              filtrosInputs.unidadesPorPallet
            ) &&
            matchesTextFilter(valores.pesoMedidas, filtrosInputs.pesoMedidas)
          );
        }),
    [filtrosInputs, palletsConDatosFiltro]
  );

  useEffect(() => {
    const topEl = tableTopScrollRef.current;
    const tableEl = tableScrollRef.current;
    const bottomEl = tableBottomScrollRef.current;

    if (!topEl || !tableEl || !bottomEl) {
      setTableScrollW(0);
      setTableClientW(0);
      return;
    }

    const syncScroll = (source, targets) => {
      const nextLeft = source.scrollLeft;
      targets.forEach((target) => {
        if (target && target.scrollLeft !== nextLeft) {
          target.scrollLeft = nextLeft;
        }
      });
    };

    const syncFromTop = () => syncScroll(topEl, [tableEl, bottomEl]);
    const syncFromTable = () => syncScroll(tableEl, [topEl, bottomEl]);
    const syncFromBottom = () => syncScroll(bottomEl, [tableEl, topEl]);

    const measure = () => {
      const nextScrollW =
        tableEl.scrollWidth ||
        tableEl.querySelector("table")?.scrollWidth ||
        0;
      const nextClientW = tableEl.clientWidth || 0;

      setTableScrollW(nextScrollW);
      setTableClientW(nextClientW);
      topEl.scrollLeft = tableEl.scrollLeft;
      bottomEl.scrollLeft = tableEl.scrollLeft;
    };

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => measure())
        : null;

    topEl.addEventListener("scroll", syncFromTop);
    tableEl.addEventListener("scroll", syncFromTable);
    bottomEl.addEventListener("scroll", syncFromBottom);
    window.addEventListener("resize", measure);
    resizeObserver?.observe(tableEl);

    const tableNode = tableEl.querySelector("table");
    if (tableNode) {
      resizeObserver?.observe(tableNode);
    }

    measure();

    return () => {
      topEl.removeEventListener("scroll", syncFromTop);
      tableEl.removeEventListener("scroll", syncFromTable);
      bottomEl.removeEventListener("scroll", syncFromBottom);
      window.removeEventListener("resize", measure);
      resizeObserver?.disconnect();
    };
  }, [clienteSel?._id, palletsVisibles.length, filtrosSel]);

  const getColumnWidth = (columnKey) =>
    columnWidths[columnKey] ?? EGRESS_COLUMN_WIDTHS[columnKey] ?? 100;

  const focusReservaCheckbox = (visibleIndex) => {
    if (visibleIndex < 0) return;
    window.requestAnimationFrame(() => {
      document.getElementById(`reservado-${visibleIndex}`)?.focus();
    });
  };

  const startColumnResize = (event, columnKey) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX ?? event.touches?.[0]?.clientX ?? 0;
    const startWidth = getColumnWidth(columnKey);

    const handlePointerMove = (moveEvent) => {
      if (typeof moveEvent.preventDefault === "function") {
        moveEvent.preventDefault();
      }
      const currentX =
        moveEvent.clientX ?? moveEvent.touches?.[0]?.clientX ?? startX;
      const nextWidth = Math.max(
        EGRESS_COLUMN_MIN_WIDTH,
        startWidth + (currentX - startX)
      );
      setColumnWidths((prev) => {
        if (prev[columnKey] === nextWidth) return prev;
        return {
          ...prev,
          [columnKey]: nextWidth,
        };
      });
    };

    const stopColumnResize = () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", stopColumnResize);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", stopColumnResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", stopColumnResize);
    window.addEventListener("touchmove", handlePointerMove, { passive: false });
    window.addEventListener("touchend", stopColumnResize);
  };

  // Maneja cambios en cada celda de la tabla de pallets
  const handlePalletChange = (index, field, value) => {
    const parseNumero = (v) => {
      const n = parseFloat(v);
      return isNaN(n) ? 0 : n; // "*" o vacio -> 0
    };

    setPallets((prev) => {
      const palletsCopy = [...prev];
      const palletActual = palletsCopy[index];

      // Caso especial: cambio de unidades en un duplicado
      if (field === "cantidad" && palletActual.esDuplicado) {
        const unidadesPrev = parseNumero(palletActual.cantidad);
        const unidadesNuevas = parseNumero(value);
        const diff = unidadesNuevas - unidadesPrev; // cuanto crecio/disminuyo

        // Buscamos el pallet original hacia arriba (el que tiene duplicarPallet === true)
        let originalIndex = -1;
        for (let i = index - 1; i >= 0; i--) {
          if (palletsCopy[i].duplicarPallet === true) {
            originalIndex = i;
            break;
          }
        }

        if (originalIndex !== -1) {
          const original = palletsCopy[originalIndex];
          const unidadesOriginalPrev = parseNumero(original.cantidad);
          let unidadesOriginalNueva = unidadesOriginalPrev - diff;

          if (unidadesOriginalNueva < 0) {
            unidadesOriginalNueva = 0; // evitar negativos
          }

          palletsCopy[originalIndex] = {
            ...original,
            cantidad: String(unidadesOriginalNueva),
          };
        }

        // Actualizo el duplicado
        palletsCopy[index] = {
          ...palletActual,
          [field]: value,
        };

        return palletsCopy;
      }

      // Caso normal para cualquier otro campo
      palletsCopy[index] = {
        ...palletActual,
        [field]: value,
      };
      return palletsCopy;
    });
  };

  // Maneja cambios de checkbox en la tabla
  const handlePalletCheckbox = (index, field, nextValue) => {
    // Caso especial: Duplicar pallet
    if (field === "duplicarPallet") {
      const palletActual = pallets[index];
      const nuevoValor = !palletActual.duplicarPallet;

      // Solo cuando pasa de false -> true
      if (nuevoValor) {
        const cuentaPosicionDuplicado = window.confirm(
          "Esta seguro que va a duplicar el pallet?"
        );
        if (cuentaPosicionDuplicado) {
          setPallets((prev) => {
            const palletsCopy = [...prev];
            const original = palletsCopy[index];

            if (original.duplicarPallet) return palletsCopy;

            // Marco el original como "es original de duplicado"
            palletsCopy[index] = {
              ...original,
              duplicarPallet: true,
              esDuplicado: false,
            };

            // Creo el duplicado
            const palletDuplicado = {
              ...original,
              duplicarPallet: false,
              esDuplicado: true, // es duplicado
              palletOriginal: original.nPallet,
              cantidad: "0",
              reserva: false,
            };

            // Inserto el duplicado debajo
            palletsCopy.splice(index + 1, 0, palletDuplicado);

            return palletsCopy;
          });
        } else {
          setPallets((prev) => {
            const palletsCopy = [...prev];
            const original = palletsCopy[index];

            if (original.duplicarPallet) return palletsCopy;

            // destildo el duplicar
            palletsCopy[index] = {
              ...original,
              duplicarPallet: false,
            };
            return palletsCopy;
          });
        }
        return;
      }
    }

    // Resto de checkboxes
    setPallets((prev) =>
      prev.map((p, i) =>
        i === index
          ? {
              ...p,
              [field]:
                typeof nextValue === "boolean" ? nextValue : !p[field],
            }
          : p
      )
    );
  };

  const startDragReserva = (index) => {
    const nextValue = !pallets[index]?.reserva;
    dragReservaRef.current = { active: true, value: nextValue };
    handlePalletCheckbox(index, "reserva", nextValue);
  };

  const dragReservaOver = (index) => {
    if (!dragReservaRef.current.active) return;
    const { value } = dragReservaRef.current;
    if (pallets[index]?.reserva === value) return;
    handlePalletCheckbox(index, "reserva", value);
  };

  const palletsReservados = useMemo(() => {
    return pallets.filter((p) => p.reserva === true);
  }, [pallets]);

  const resumenReserva = useMemo(() => {
    return palletsReservados.reduce(
      (acc, p) => {
        acc.pallets += 1;
        acc.unidades += toNumber(p.cantidad);
        return acc;
      },
      { pallets: 0, unidades: 0 }
    );
  }, [palletsReservados]);

  // llamar a la api para crear la reserva
  const handleCompletarReserva = async () => {
    if (!clienteSel?._id) {
      alert("Seleccione un cliente valido.");
      return;
    }

    if (palletsReservados.length === 0) {
      alert("No hay pallets reservados.");
      return;
    }

    const payload = [];

    for (const p of palletsReservados) {
      const cantidadReservada = toNumber(p.cantidad);
      if (cantidadReservada <= 0) continue;

      const disponible = Number.isFinite(Number(p.disponible))
        ? Number(p.disponible)
        : toNumber(p.cantidad);

      const numeroRemito = p.ingreso?.nroRemito || p.ingreso?.remito;

      if (!numeroRemito) {
        alert("Hay pallets sin numero de remito.");
        return;
      }

      if (cantidadReservada > disponible) {
        alert(
          `Stock insuficiente en pallet ${p.nPallet || p.identificacion}. Disponible ${disponible}, solicitado ${cantidadReservada}`
        );
        return;
      }

      payload.push({
        palletId: p._id,
        nPallet: p.nPallet,
        identificacion: p.identificacion,
        numeroPalet: p.nPallet,
        numeroLote: p.lote?.nroLote,
        numeroRemito,
        descripcion:
          p?.producto?.detalleProducto ||
          p?.producto?.nombreProducto ||
          "Producto",
        cantidadDisponible: disponible,
        cantidadReservada,
      });
    }

    if (payload.length === 0) {
      alert("No hay pallets con cantidad valida.");
      return;
    }

    try {
      await api.post(API.RESERVAR, {
        clienteId: clienteSel._id,
        deposito: depositoActual,
        pallets: payload,
      });
      alert("Reserva confirmada. Se creo un pedido pendiente.");
      setMostrarModal(false);
      fetchPallets(clienteSel._id);
    } catch (error) {
      console.error("POST /api/egresos/carrito/reservar", error);
      alert(
        error?.response?.data?.error ||
          "Hubo un error al confirmar la reserva."
      );
    }
  };

  const cerrarModal = () => {
    setMostrarModal(false);
  };

  const showHorizontalScrollbars = tableScrollW > tableClientW;

  const renderHeaderCell = (
    columnKey,
    label,
    className = "col-xs",
    extraProps = {}
  ) => {
    const { className: extraClassName = "", ...restProps } = extraProps;
    return (
      <th
        className={`${className} egreso-resizable-th ${extraClassName}`.trim()}
        style={buildColStyle(getColumnWidth(columnKey))}
        {...restProps}
      >
        <span className="egreso-th-label">{label}</span>
        <button
          type="button"
          className="egreso-col-resizer"
          tabIndex={-1}
          aria-hidden="true"
          onMouseDown={(event) => startColumnResize(event, columnKey)}
          onTouchStart={(event) => startColumnResize(event, columnKey)}
        />
      </th>
    );
  };

  return (
    <div className="container my-4 ingreso-wrap">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h3 className="mb-0 text-white">Reserva</h3>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate("/pedidos")}
          >
            Pedidos
          </button>
          <button
            type="button"
            className="btn btn-outline-success btn-sm"
            onClick={() => fetchPallets(clienteSel?._id)}
            disabled={!clienteSel || loadingPallets}
          >
            {loadingPallets ? "Cargando..." : "Actualizar"}
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-6">
              <label className="form-label">Cliente</label>
              <div className="cliente-autocomplete">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por codigo, nombre o CUIT"
                  value={clienteQuery}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;

                    const queryCode = normalizeClienteCode(clienteQuery);
                    if (!queryCode || clientesSugeridos.length !== 1) return;

                    const clienteExacto = clientesSugeridos[0];
                    if (
                      normalizeClienteCode(clienteExacto?.codCliente) !== queryCode
                    ) {
                      return;
                    }

                    e.preventDefault();
                    handleSelectCliente(clienteExacto);
                  }}
                  onChange={(e) => {
                    setClienteQuery(e.target.value);
                    setClienteSel(null);
                    setFiltrosSel([]);
                    resetFiltrosInputs();
                  }}
                />
                {buscandoCliente && (
                  <div className="cliente-estado">Buscando...</div>
                )}
                {!clienteSeleccionadoCoincide &&
                  clienteQuery.trim().length >= 1 &&
                  clientesSugeridos.length > 0 && (
                  <div className="cliente-list">
                    {clientesSugeridos.map((c) => (
                      <div
                        key={c._id}
                        className="cliente-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectCliente(c);
                        }}
                      >
                        <strong>{c.nombreCliente}</strong>{" "}
                        {(c.codCliente || c.CUIT) && (
                          <span className="cliente-sub">
                            {[
                              c.codCliente ? `Codigo ${c.codCliente}` : null,
                              c.CUIT ? `CUIT ${c.CUIT}` : null,
                            ]
                              .filter(Boolean)
                              .join(" | ")}
                          </span>
                        )}
                        {Array.isArray(c.Filtros) && c.Filtros.length > 0 && (
                          <div className="cliente-sub">
                            Filtros: {normalizarFiltros(c.Filtros).join(", ")}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="cliente-estado">
                {!clienteSel
                  ? "Seleccione un cliente para ver pallets."
                  : tieneFiltrosConfigurados
                    ? `Mostrando filtros configurados para ${clienteSel.nombreCliente || "el cliente"}.`
                    : "Cliente sin filtros configurados. Busqueda por Nro Remito disponible por defecto."}
                {clienteSel && pallets.length > 0 && (
                  <div>
                    Mostrando {palletsVisibles.length} de {pallets.length} pallet
                    {pallets.length === 1 ? "" : "s"}.
                  </div>
                )}
              </div>
            </div>
          </div>
          {clienteSel && (
            <div className="egreso-filtros-grid">
              <div className="egreso-filtro">
                <label className="form-label">Nro Remito</label>
                <input
                  type="text"
                  className="form-control"
                  name="nroRemito"
                  value={filtrosInputs.nroRemito}
                  onChange={handleFiltroInputChange}
                />
              </div>
              {tieneFiltrosConfigurados && mostrarCampo("Ubicacion") && (
                <div className="egreso-filtro">
                  <label className="form-label">Ubicacion</label>
                  <select
                    className="form-select"
                    name="ubicacion"
                    value={filtrosInputs.ubicacion}
                    onChange={handleFiltroInputChange}
                  >
                    <option value="">Seleccionar...</option>
                    {ubicacionesDisponibles.map((ubicacion) => (
                      <option key={ubicacion} value={ubicacion}>
                        {ubicacion}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Planta") && (
                <div className="egreso-filtro">
                  <label className="form-label">Planta</label>
                  <select
                    className="form-select"
                    name="planta"
                    value={filtrosInputs.planta}
                    onChange={handleFiltroInputChange}
                  >
                    <option value="">Seleccionar...</option>
                    {plantasDisponibles.map((planta) => (
                      <option key={planta} value={planta}>
                        {planta}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Unidad de negocio") && (
                <div className="egreso-filtro">
                  <label className="form-label">Unidad de negocio</label>
                  <select
                    className="form-select"
                    name="unidadNegocio"
                    value={filtrosInputs.unidadNegocio}
                    onChange={handleFiltroInputChange}
                  >
                    <option value="">Seleccionar...</option>
                    {unidadesNegocioDisponibles.map((unidad) => (
                      <option key={unidad} value={unidad}>
                        {unidad}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Lote") && (
                <div className="egreso-filtro">
                  <label className="form-label">Lote</label>
                  <input
                    type="text"
                    className="form-control"
                    name="lote"
                    value={filtrosInputs.lote}
                    onChange={handleFiltroInputChange}
                  />
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Codigo") && (
                <div className="egreso-filtro">
                  <label className="form-label">Codigo</label>
                  <input
                    type="text"
                    className="form-control"
                    name="codigo"
                    value={filtrosInputs.codigo}
                    onChange={handleFiltroInputChange}
                  />
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Detalle de producto / N\u00ba FC") && (
                <div className="egreso-filtro egreso-filtro--wide">
                  <label className="form-label">Detalle de producto / Nro. FC</label>
                  <input
                    type="text"
                    className="form-control"
                    name="detalleProducto"
                    value={filtrosInputs.detalleProducto}
                    onChange={handleFiltroInputChange}
                  />
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Fecha ingreso") && (
                <div className="egreso-filtro">
                  <label className="form-label">Fecha ingreso</label>
                  <input
                    type="date"
                    className="form-control"
                    name="fechaIngreso"
                    value={filtrosInputs.fechaIngreso}
                    onChange={handleFiltroInputChange}
                  />
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Fecha elaboracion") && (
                <div className="egreso-filtro">
                  <label className="form-label">Fecha elaboracion</label>
                  <input
                    type="date"
                    className="form-control"
                    name="fechaElaboracion"
                    value={filtrosInputs.fechaElaboracion}
                    onChange={handleFiltroInputChange}
                  />
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Fecha vencimiento") && (
                <div className="egreso-filtro">
                  <label className="form-label">Fecha vencimiento</label>
                  <input
                    type="date"
                    className="form-control"
                    name="fechaVencimiento"
                    value={filtrosInputs.fechaVencimiento}
                    onChange={handleFiltroInputChange}
                  />
                </div>
              )}
              {tieneFiltrosConfigurados &&
                (mostrarCampo("Tipo de embalaje") ||
                  mostrarCampo("Tipo embalaje")) && (
                <div className="egreso-filtro">
                  <label className="form-label">Tipo embalaje</label>
                  <input
                    type="text"
                    className="form-control"
                    name="tipoEmbalaje"
                    value={filtrosInputs.tipoEmbalaje}
                    onChange={handleFiltroInputChange}
                  />
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Precinto SENASA") && (
                <div className="egreso-filtro">
                  <label className="form-label">Precinto SENASA</label>
                  <input
                    type="text"
                    className="form-control"
                    name="recintoSenasa"
                    value={filtrosInputs.recintoSenasa}
                    onChange={handleFiltroInputChange}
                  />
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Condicion") && (
                <div className="egreso-filtro">
                  <label className="form-label">Condicion</label>
                  <select
                    className="form-select"
                    name="condicion"
                    value={filtrosInputs.condicion}
                    onChange={handleFiltroInputChange}
                  >
                    <option value="">Todas</option>
                    <option value="OK">OK</option>
                    <option value="ROTAS">Rotas</option>
                  </select>
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Observaciones") && (
                <div className="egreso-filtro egreso-filtro--wide">
                  <label className="form-label">Observaciones</label>
                  <input
                    type="text"
                    className="form-control"
                    name="observaciones"
                    value={filtrosInputs.observaciones}
                    onChange={handleFiltroInputChange}
                  />
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Unidades por pallet") && (
                <div className="egreso-filtro">
                  <label className="form-label">Unidades por pallet</label>
                  <input
                    type="text"
                    className="form-control"
                    name="unidadesPorPallet"
                    value={filtrosInputs.unidadesPorPallet}
                    onChange={handleFiltroInputChange}
                  />
                </div>
              )}
              {tieneFiltrosConfigurados && mostrarCampo("Peso / Medidas") && (
                <div className="egreso-filtro">
                  <label className="form-label">Peso / Medidas</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pesoMedidas"
                    value={filtrosInputs.pesoMedidas}
                    onChange={handleFiltroInputChange}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {clienteSel && (
        <>
          <div
            className="ingreso-table-scrollbar"
            ref={tableTopScrollRef}
            style={{ display: showHorizontalScrollbars ? "block" : "none" }}
            aria-hidden="true"
          >
            <div style={{ width: tableScrollW, height: 1 }} />
          </div>
          <div className="table-responsive egreso-table-viewport" ref={tableScrollRef}>
            <table className="table table-sm table-striped mb-0 tabla-pallets egreso-maqueta-table">
              <colgroup>
                <col style={buildColStyle(getColumnWidth("nroRemito"))} />
                {mostrarCampo("Ubicacion") && (
                  <col style={buildColStyle(getColumnWidth("ubicacion"))} />
                )}
                <col style={buildColStyle(getColumnWidth("cliente"))} />
                {mostrarCampo("Fecha ingreso") && (
                  <col style={buildColStyle(getColumnWidth("fechaIngreso"))} />
                )}
                {mostrarCampo("Planta") && (
                  <col style={buildColStyle(getColumnWidth("procedencia"))} />
                )}
                {mostrarCampo("Unidad de negocio") && (
                  <col style={buildColStyle(getColumnWidth("unidadNegocio"))} />
                )}
                <col style={buildColStyle(getColumnWidth("compIng"))} />
                {mostrarCampo("Detalle de producto / N\u00ba FC") && (
                  <col
                    style={buildColStyle(getColumnWidth("fcGrupoProveedor"))}
                  />
                )}
                {mostrarCampo("Fecha elaboracion") && (
                  <col style={buildColStyle(getColumnWidth("fechaElab"))} />
                )}
                {mostrarCampo("Fecha vencimiento") && (
                  <col style={buildColStyle(getColumnWidth("fechaVto"))} />
                )}
                {mostrarCampo("Codigo") && (
                  <col style={buildColStyle(getColumnWidth("codigo"))} />
                )}
                {mostrarCampo("Detalle de producto / N\u00ba FC") && (
                  <col style={buildColStyle(getColumnWidth("detalleFc"))} />
                )}
                {mostrarCampo("Lote") && (
                  <col style={buildColStyle(getColumnWidth("nroLote"))} />
                )}
                <col style={buildColStyle(getColumnWidth("id"))} />
                <col style={buildColStyle(getColumnWidth("nroPallet"))} />
                {(mostrarCampo("Tipo de embalaje") ||
                  mostrarCampo("Tipo embalaje")) && (
                  <col style={buildColStyle(getColumnWidth("tipoEmbalaje"))} />
                )}
                {mostrarCampo("Precinto SENASA") && (
                  <col style={buildColStyle(getColumnWidth("senasa"))} />
                )}
                {mostrarCampo("Unidades por pallet") && (
                  <col style={buildColStyle(getColumnWidth("unidPallet"))} />
                )}
                {mostrarCampo("Peso / Medidas") && (
                  <col style={buildColStyle(getColumnWidth("pesoMedidas"))} />
                )}
                {mostrarCampo("Detalle de producto / N\u00ba FC") && (
                  <col style={buildColStyle(getColumnWidth("cantFc"))} />
                )}
                {mostrarCampo("Condicion") && (
                  <col style={buildColStyle(getColumnWidth("daniosIn"))} />
                )}
                {mostrarCampo("Condicion") && (
                  <col style={buildColStyle(getColumnWidth("motivoDanio"))} />
                )}
                {mostrarCampo("Observaciones") && (
                  <col style={buildColStyle(getColumnWidth("obsIn"))} />
                )}
                <col style={buildColStyle(getColumnWidth("reservado"))} />
              </colgroup>
              <thead className="table-light">
                <tr>
                  {renderHeaderCell("nroRemito", "Nro Remito")}
                  {mostrarCampo("Ubicacion") && (
                    renderHeaderCell("ubicacion", "Ubicacion Binder")
                  )}
                  {renderHeaderCell("cliente", "Cliente")}
                  {mostrarCampo("Fecha ingreso") && (
                    renderHeaderCell("fechaIngreso", "Fecha Ingreso")
                  )}
                  {mostrarCampo("Planta") && (
                    renderHeaderCell("procedencia", "Procedencia/ Planta")
                  )}
                  {mostrarCampo("Unidad de negocio") && (
                    renderHeaderCell("unidadNegocio", "Unidad de Negocio")
                  )}
                  {renderHeaderCell("compIng", "Comp Ing")}
                  {mostrarCampo("Detalle de producto / N\u00ba FC") && (
                    renderHeaderCell(
                      "fcGrupoProveedor",
                      "FC/ Grupo/ Proveedor"
                    )
                  )}
                  {mostrarCampo("Fecha elaboracion") && (
                    renderHeaderCell("fechaElab", "F. elab.", "col-s")
                  )}
                  {mostrarCampo("Fecha vencimiento") && (
                    renderHeaderCell("fechaVto", "F. vto", "col-s")
                  )}
                  {mostrarCampo("Codigo") && (
                    renderHeaderCell("codigo", "Codigo")
                  )}
                  {mostrarCampo("Detalle de producto / N\u00ba FC") && (
                    renderHeaderCell("detalleFc", "Detalle / Nro FC", "col-l")
                  )}
                  {mostrarCampo("Lote") && (
                    renderHeaderCell("nroLote", "Nro lote")
                  )}
                  {renderHeaderCell("id", "ID")}
                  {renderHeaderCell("nroPallet", "Nro pallet")}
                  {(mostrarCampo("Tipo de embalaje") ||
                    mostrarCampo("Tipo embalaje")) && (
                    renderHeaderCell("tipoEmbalaje", "Tipo embalaje")
                  )}
                  {mostrarCampo("Precinto SENASA") && (
                    renderHeaderCell("senasa", "Senasa")
                  )}
                  {mostrarCampo("Unidades por pallet") && (
                    renderHeaderCell("unidPallet", "Unid. x pallet")
                  )}
                  {mostrarCampo("Peso / Medidas") && (
                    renderHeaderCell("pesoMedidas", "Peso/ medidas")
                  )}
                  {mostrarCampo("Detalle de producto / N\u00ba FC") && (
                    renderHeaderCell("cantFc", "Cant x FC")
                  )}
                  {mostrarCampo("Condicion") && (
                    renderHeaderCell("daniosIn", "Danios In")
                  )}
                  {mostrarCampo("Condicion") && (
                    renderHeaderCell("motivoDanio", "Motivo danio in")
                  )}
                  {mostrarCampo("Observaciones") && (
                    renderHeaderCell("obsIn", "Obs in", "col-l")
                  )}
                  {renderHeaderCell("reservado", "Reservado", "col-xs", {
                    tabIndex: 0,
                    onKeyDown: (event) => {
                      if (event.key === "ArrowDown") {
                        event.preventDefault();
                        focusReservaCheckbox(0);
                      }
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (palletsVisibles.length === 0) return;
                        handlePalletCheckbox(palletsVisibles[0].index, "reserva");
                        focusReservaCheckbox(
                          palletsVisibles.length > 1 ? 1 : 0
                        );
                      }
                    },
                  })}
                </tr>
              </thead>
              <tbody>
                {palletsVisibles.length === 0 ? (
                  <tr>
                    <td colSpan={99} className="text-center">
                      {loadingPallets
                        ? "Cargando..."
                        : pallets.length === 0
                          ? "No hay pallets para mostrar."
                          : "No hay pallets que coincidan con los filtros."}
                    </td>
                  </tr>
                ) : (
                  palletsVisibles.map(({ item: p, index }, visibleIndex) => (
                    <tr
                      className={`text-center ${p.reserva ? "row-reserva" : ""}`}
                      key={`${p._id}-${index}`}
                    >
                      <td>
                        {" "}
                        {p.ingreso?.nroRemito || p.ingreso?.remito || ""}{" "}
                      </td>
                      {mostrarCampo("Ubicacion") && (
                        <td> {p.ubicacion} </td>
                      )}
                      <td>{p.ingreso?.clienteNombre || ""}</td>
                      {mostrarCampo("Fecha ingreso") && (
                        <td> {fmtDate(p.fechaIngreso)}</td>
                      )}
                      {mostrarCampo("Planta") && (
                        <td> {p.procedenciaPlanta}</td>
                      )}
                      {mostrarCampo("Unidad de negocio") && (
                        <td> {p.unidad}</td>
                      )}
                      <td> </td>
                      {mostrarCampo("Detalle de producto / N\u00ba FC") && (
                        <td> </td>
                      )}
                      {mostrarCampo("Fecha elaboracion") && (
                        <td> {fmtDate(p.lote?.fechaFab)}</td>
                      )}
                      {mostrarCampo("Fecha vencimiento") && (
                        <td> {fmtDate(p.lote?.fechaVto)}</td>
                      )}
                      {mostrarCampo("Codigo") && (
                        <td> {p.producto?.codProducto}</td>
                      )}
                      {mostrarCampo("Detalle de producto / N\u00ba FC") && (
                        <td> {p.producto?.detalleProducto}</td>
                      )}
                      {mostrarCampo("Lote") && <td> {p.lote?.nroLote}</td>}
                      <td>{p.identificacion}</td>
                      <td>{p.nPallet}</td>
                      {(mostrarCampo("Tipo de embalaje") ||
                        mostrarCampo("Tipo embalaje")) && (
                        <td>{p.tipoEmbalaje}</td>
                      )}
                      {mostrarCampo("Precinto SENASA") && (
                        <td>{p.senasaCodigo}</td>
                      )}
                      {mostrarCampo("Unidades por pallet") && (
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={p.cantidad}
                            onChange={(e) =>
                              handlePalletChange(
                                index,
                                "cantidad",
                                e.target.value
                              )
                            }
                          />
                        </td>
                      )}
                      {mostrarCampo("Peso / Medidas") && (
                        <td>
                          {p.producto?.peso && p.producto?.medidas
                            ? p.producto.peso / p.producto.medidas
                            : ""}
                        </td>
                      )}
                      {mostrarCampo("Detalle de producto / N\u00ba FC") && (
                        <td>{p.producto?.cantidadxfc}</td>
                      )}
                      {mostrarCampo("Condicion") && <td></td>}
                      {mostrarCampo("Condicion") && <td></td>}
                      {mostrarCampo("Observaciones") && <td></td>}
                      <td
                        className="text-center reserva-cell"
                        onMouseDown={(e) => {
                          if (e.button !== 0) return;
                          e.preventDefault();
                          startDragReserva(index);
                        }}
                        onMouseEnter={() => dragReservaOver(index)}
                      >
                        <input
                          type="checkbox"
                          id={`reservado-${visibleIndex}`}
                          tabIndex={0}
                          checked={p.reserva}
                          onClick={(e) => e.preventDefault()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handlePalletCheckbox(index, "reserva");
                              const nextVisibleIndex =
                                visibleIndex + 1 < palletsVisibles.length
                                  ? visibleIndex + 1
                                  : visibleIndex;
                              focusReservaCheckbox(nextVisibleIndex);
                            }
                            if (e.key === " ") {
                              e.preventDefault();
                              handlePalletCheckbox(index, "reserva");
                            }
                            if (e.key === "ArrowDown") {
                              e.preventDefault();
                              focusReservaCheckbox(visibleIndex + 1);
                            }
                            if (e.key === "ArrowUp") {
                              e.preventDefault();
                              focusReservaCheckbox(visibleIndex - 1);
                            }
                          }}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div
            className="ingreso-table-scrollbar ingreso-table-scrollbar--bottom"
            ref={tableBottomScrollRef}
            style={{ display: showHorizontalScrollbars ? "block" : "none" }}
            aria-hidden="true"
          >
            <div style={{ width: tableScrollW, height: 1 }} />
          </div>

          <div className="card-footer mt-3">
            <div className="text-end">
              <button
                type="button"
                className="btn btn-success"
                onClick={() => setMostrarModal(true)}
              >
                Finalizar
              </button>
            </div>
          </div>
        </>
      )}

      {/* MODAL DE RESUMEN */}
      {mostrarModal && (
        <div
          className="ingreso-modal-overlay"
          onClick={cerrarModal}
        >
          <div
            className="modal-dialog ingreso-modal ingreso-modal--confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="egreso-resumen-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="egreso-resumen-title">
                  Resumen por lote
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cerrarModal}
                ></button>
              </div>
              <div className="modal-body">
                {pallets.length === 0 ? (
                  <p className="egreso-modal-empty">No hay pallets para mostrar.</p>
                ) : (
                  <div className="egreso-modal-summary">
                    <hr />
                    <div className="egreso-modal-summary__card">
                      <div className="egreso-modal-summary__title">Reservados</div>
                      <div className="egreso-modal-summary__metrics">
                        <div className="egreso-modal-summary__metric">
                          <span>Pallets</span>
                          <strong>{resumenReserva.pallets}</strong>
                        </div>
                        <div className="egreso-modal-summary__metric">
                          <span>Unidades</span>
                          <strong>{resumenReserva.unidades}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cerrarModal}
                >
                  Cerrar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCompletarReserva}
                >
                  Reservar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="text-dark"
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          background: "#ffffff",
          border: "1px solid",
          borderColor: resumenReserva.pallets > 0 ? "#198754" : "#ddd",
          borderRadius: "8px",
          padding: "10px 14px",
          minWidth: "160px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          zIndex: 9999,
          fontSize: "14px",
        }}
      >
        <strong>Reservados</strong>
        <div>Pallets: {resumenReserva.pallets}</div>
        <div>Unidades: {resumenReserva.unidades}</div>
      </div>
    </div>
  );
};

export default EgresoMaqueta;
